import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Check, Crown } from "lucide-react";

export default function Register() {
  const { registerJwt } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    api.get("/membership/tiers").then(({ data }) => setTiers(data)).catch(() => {});
  }, []);

  const createAccount = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await registerJwt(email, password, name);
      toast.success("Account created — now pick your membership");
      setStep(2);
    } catch (err) {
      toast.error(apiError(err, "Registration failed"));
    } finally {
      setBusy(false);
    }
  };

  const chooseTier = async (tier) => {
    if (tier.id === "basic") {
      toast.success("Welcome to the ecosystem!");
      nav("/ecosystem");
      return;
    }
    setBusy(true);
    try {
      await api.post("/membership/subscribe", { tier: tier.id });
      toast.success(`${tier.name} membership active!`);
      nav("/ecosystem");
    } catch (err) {
      toast.error(apiError(err, "Subscription failed"));
      toast.info("Deposit USDT to your wallet first, then subscribe from the Passes page.");
      nav("/wallet");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center p-4 scanlines" data-testid="register-page">
      <div className="w-full max-w-4xl">
        <div className="text-center mb-8">
          <div className="label-mono text-brand-magenta mb-2">// join_the_ecosystem</div>
          <h1 className="font-display font-black text-3xl lg:text-5xl tracking-tighter">
            {step === 1 ? "CREATE YOUR ACCOUNT." : "PICK YOUR MEMBERSHIP."}
          </h1>
          <div className="flex items-center justify-center gap-2 mt-4">
            <span className={`w-8 h-1.5 ${step >= 1 ? "bg-brand-magenta" : "bg-white/15"}`} />
            <span className={`w-8 h-1.5 ${step >= 2 ? "bg-brand-magenta" : "bg-white/15"}`} />
          </div>
        </div>

        {step === 1 && (
          <div className="grid lg:grid-cols-2 gap-6 max-w-4xl mx-auto items-stretch">
            <div className="relative hidden lg:block panel overflow-hidden" data-testid="register-visual">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/1c220779-6edd-4bd3-b2c7-23a9d0486762/images/ed4f43a390f3ecd44892b9a4b1a9026ef138f5ffa2dd28b5768888e26a3afbd5.jpeg"
                alt="Miku Halo"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-900/90 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="label-mono text-brand-amber">// your_new_identity</div>
                <div className="font-display font-black text-2xl">MIKU HALO</div>
                <p className="text-white/60 text-xs font-body mt-1">Pick from 16 Japanese cyberpunk characters after you join — humans, oni and dragon deities.</p>
              </div>
            </div>
            <form onSubmit={createAccount} className="panel p-6 lg:p-8 space-y-4" data-testid="register-form">
            <div>
              <label className="label-mono">Handle</label>
              <input className="field mt-2" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} placeholder="player_one" data-testid="register-name-input" />
            </div>
            <div>
              <label className="label-mono">Email</label>
              <input type="email" className="field mt-2" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" data-testid="register-email-input" />
            </div>
            <div>
              <label className="label-mono">Password</label>
              <input type="password" className="field mt-2" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} placeholder="••••••••" data-testid="register-password-input" />
            </div>
            <div>
              <label className="label-mono">Confirm Password</label>
              <input type="password" className="field mt-2" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} placeholder="••••••••" data-testid="register-confirm-input" />
            </div>
            <button type="submit" disabled={busy} className="btn-neon w-full" data-testid="register-submit-button">
              {busy ? "…" : "CONTINUE →"}
            </button>
            <p className="text-center text-white/40 text-xs font-mono">
              Already a citizen? <Link to="/login" className="text-brand-magenta hover:underline" data-testid="register-login-link">Log in</Link>
            </p>
            </form>
          </div>
        )}

        {step === 2 && (
          <div data-testid="register-tier-step">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {tiers.map((t) => (
                <div key={t.id} className={`panel p-6 flex flex-col ${t.id === "gold" ? "border-brand-amber" : t.id === "premium" ? "border-brand-magenta" : ""}`} data-testid={`register-tier-${t.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-display font-black text-xl">{t.name}</div>
                    {t.id === "gold" && <Crown className="w-5 h-5 text-brand-amber" />}
                  </div>
                  <div className="font-mono text-3xl font-black mt-2">
                    {t.price_usdt === 0 ? "FREE" : <>{t.price_usdt} <span className="text-xs text-white/40">USDT/mo</span></>}
                  </div>
                  <ul className="mt-4 space-y-2 flex-1">
                    {(t.perks || []).map((p) => (
                      <li key={p} className="text-xs font-mono text-white/55 flex items-start gap-2">
                        <Check className="w-3.5 h-3.5 text-brand-magenta shrink-0 mt-0.5" /> {p}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => chooseTier(t)}
                    disabled={busy}
                    className={`w-full !py-3 text-xs mt-6 ${t.id === "basic" ? "btn-ghost" : "btn-neon"}`}
                    data-testid={`choose-tier-${t.id}`}
                  >
                    {busy ? "…" : t.price_usdt === 0 ? "START FREE" : `JOIN · ${t.price_usdt} USDT/mo`}
                  </button>
                </div>
              ))}
            </div>
            <p className="text-center text-white/40 text-xs font-mono mt-6">
              Paid tiers require USDT balance — you can also upgrade later from the Passes page.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
